import React, { useMemo } from "react";
import {
    Button,
    Typography,
    TablePagination,
    Stack,
    Box,
    LinearProgress,
    TextField,
    InputAdornment,
    Menu,
    Radio,
    RadioGroup,
    Checkbox,
    IconButton,
    FormControlLabel,
    FormControl,
    FormLabel
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import {
    tableTheme,
    tableConfigTheme,
    dropDownMenuTheme,
    paginationTheme
} from "../../styles/ListViewTheme.js";
import { mbxUserAtom } from "../../atoms/atoms";
import {
    webhooksPaginationAtom,
    setWebhooksPageAtom,
    setWebhooksPerPageAtom,
    openAddWebhookModalAtom,
    webhooksSearchAtom,
    webhooksEventTypeFilterAtom,
    webhooksStatusFilterAtom,
    webhooksSortAtom,
    WEBHOOK_EVENT_TYPE_OPTIONS,
    WEBHOOK_EVENT_TYPE_LABELS,
    WEBHOOK_STATUS_OPTIONS
} from "../../atoms/webhooks";
import { getJsonApi } from "../../modules/AmplifyServices";
import { WebhooksTable } from "./WebhooksTable";
import AddWebhookModal from "./AddWebhookModal";
import ViewWebhookModal from "./ViewWebhookModal";
import { DialogTheme } from "../../styles/DialogTheme.js";

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

// Sorting comparator function
const getComparator = (orderBy, order) => {
    return (a, b) => {
        let aValue = a[orderBy];
        let bValue = b[orderBy];

        // Special handling for status - active should come first when desc
        if (orderBy === "status") {
            aValue = aValue === "active" ? 1 : 0;
            bValue = bValue === "active" ? 1 : 0;
        }

        // Handle dates
        if (orderBy === "createdAt") {
            aValue = new Date(aValue).getTime();
            bValue = new Date(bValue).getTime();
        }

        // Handle strings - case insensitive
        if (typeof aValue === "string" && typeof bValue === "string") {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
        }

        if (bValue < aValue) {
            return order === "desc" ? -1 : 1;
        }
        if (bValue > aValue) {
            return order === "desc" ? 1 : -1;
        }
        return 0;
    };
};

// Multi-level sort for default ordering
const defaultSort = (webhooks) => {
    return [...webhooks].sort((a, b) => {
        // First: status (active first)
        const statusA = a.status === "active" ? 1 : 0;
        const statusB = b.status === "active" ? 1 : 0;
        if (statusB !== statusA) return statusB - statusA;

        // Second: eventType (alphabetical)
        const eventCompare = a.eventType.localeCompare(b.eventType);
        if (eventCompare !== 0) return eventCompare;

        // Third: createdAt (newest first)
        return new Date(b.createdAt) - new Date(a.createdAt);
    });
};

export const WebhooksPage = () => {
    const [mbxUser] = useAtom(mbxUserAtom);
    const { companyId } = mbxUser;
    const [paginationConfig] = useAtom(webhooksPaginationAtom);
    const [, setPerPageAction] = useAtom(setWebhooksPerPageAtom);
    const [, setPageAction] = useAtom(setWebhooksPageAtom);
    const { page, perPage } = paginationConfig;
    const theme = useTheme();

    // Search and filter state
    const [searchQuery, setSearchQuery] = useAtom(webhooksSearchAtom);
    const [eventTypeFilter, setEventTypeFilter] = useAtom(
        webhooksEventTypeFilterAtom
    );
    const [statusFilter, setStatusFilter] = useAtom(webhooksStatusFilterAtom);
    const [sortConfig, setSortConfig] = useAtom(webhooksSortAtom);

    const {
        data: webhooks = [],
        isLoading,
        refetch
    } = useQuery({
        queryKey: ["webhooks", companyId],
        queryFn: async () => {
            try {
                const response = await getJsonApi(
                    "MB-rest-api",
                    "/v1/mb/webhooks",
                    {
                        queryParams: {
                            companyId,
                            includeInactive: "true" // Include inactive webhooks
                        }
                    }
                );
                return response?.webhooks || [];
            } catch (e) {
                console.error("Error fetching webhooks:", e);
                return [];
            }
        },
        enabled: !!companyId
    });

    // Filter and sort webhooks
    const filteredAndSortedWebhooks = useMemo(() => {
        let result = [...webhooks];

        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            result = result.filter((webhook) => {
                const nameMatch = webhook.name?.toLowerCase().includes(query);
                const eventTypeMatch = webhook.eventType
                    ?.toLowerCase()
                    .includes(query);
                const eventLabelMatch = WEBHOOK_EVENT_TYPE_LABELS[
                    webhook.eventType
                ]
                    ?.toLowerCase()
                    .includes(query);
                return nameMatch || eventTypeMatch || eventLabelMatch;
            });
        }

        // Apply event type filter
        if (eventTypeFilter.length > 0) {
            result = result.filter((webhook) =>
                eventTypeFilter.includes(webhook.eventType)
            );
        }

        // Apply status filter
        if (statusFilter !== "all") {
            result = result.filter(
                (webhook) => webhook.status === statusFilter
            );
        }

        // Apply sorting
        if (sortConfig.orderBy === "status" && sortConfig.order === "desc") {
            // Use default multi-level sort
            result = defaultSort(result);
        } else {
            result.sort(getComparator(sortConfig.orderBy, sortConfig.order));
        }

        return result;
    }, [webhooks, searchQuery, eventTypeFilter, statusFilter, sortConfig]);

    // Handle sort change
    const handleSortChange = (column) => {
        setSortConfig((prev) => {
            if (prev.orderBy === column) {
                // Toggle order if same column
                return {
                    ...prev,
                    order: prev.order === "asc" ? "desc" : "asc"
                };
            }
            // Default to desc for new column (except name which defaults to asc)
            return {
                orderBy: column,
                order: column === "name" ? "asc" : "desc"
            };
        });
    };

    // Paginate
    const paginatedWebhooks = filteredAndSortedWebhooks.slice(
        perPage * page,
        perPage * (page + 1)
    );

    // Reset page when filters change
    const handleSearchChange = (value) => {
        setSearchQuery(value);
        setPageAction(0);
    };

    return (
        <>
            {isLoading ? (
                <Box sx={{ width: "100%" }}>
                    <LinearProgress />
                </Box>
            ) : (
                <ThemeProvider theme={tableTheme(theme)}>
                    <TableHeader
                        refetch={refetch}
                        searchQuery={searchQuery}
                        onSearchChange={handleSearchChange}
                        eventTypeFilter={eventTypeFilter}
                        setEventTypeFilter={setEventTypeFilter}
                        statusFilter={statusFilter}
                        setStatusFilter={setStatusFilter}
                        setPageAction={setPageAction}
                    />

                    <Box
                        sx={{
                            flexGrow: 1,
                            display: "flex",
                            flexDirection: "column"
                        }}
                    >
                        {paginatedWebhooks.length === 0 ? (
                            <>
                                <WebhooksTable
                                    webhooks={[]}
                                    refetch={refetch}
                                    sortConfig={sortConfig}
                                    onSortChange={handleSortChange}
                                />
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        height: "70vh"
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        color={theme.palette.text.secondary}
                                    >
                                        {webhooks.length === 0
                                            ? "No webhooks found"
                                            : "No webhooks match your filters"}
                                    </Typography>
                                </Box>
                            </>
                        ) : (
                            <WebhooksTable
                                webhooks={paginatedWebhooks}
                                refetch={refetch}
                                sortConfig={sortConfig}
                                onSortChange={handleSortChange}
                            />
                        )}
                    </Box>

                    <ThemeProvider theme={paginationTheme(theme)}>
                        <Box
                            sx={{
                                bottom: 0,
                                backgroundColor: theme.palette.background.paper
                            }}
                        >
                            <TablePagination
                                count={filteredAndSortedWebhooks?.length || 0}
                                component={"div"}
                                page={page}
                                onPageChange={(e, page) => setPageAction(page)}
                                onRowsPerPageChange={({
                                    target: { value: perPage }
                                }) => setPerPageAction(perPage)}
                                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                                rowsPerPage={perPage}
                                labelRowsPerPage=""
                            />
                        </Box>
                    </ThemeProvider>
                </ThemeProvider>
            )}

            <ThemeProvider theme={DialogTheme(theme)}>
                <AddWebhookModal refetch={refetch} />
            </ThemeProvider>
            <ViewWebhookModal refetch={refetch} />
        </>
    );
};

const TableHeader = ({
    refetch,
    searchQuery,
    onSearchChange,
    eventTypeFilter,
    setEventTypeFilter,
    statusFilter,
    setStatusFilter,
    setPageAction
}) => {
    const [, setOpenAddWebhookModal] = useAtom(openAddWebhookModalAtom);
    const themePalette = useTheme().palette;

    const handleClearSearch = () => {
        onSearchChange("");
    };

    return (
        <ThemeProvider theme={tableConfigTheme(themePalette)}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ marginBottom: "15px" }}
            >
                <Stack
                    direction="row"
                    gap={"12px"}
                    sx={{ textAlign: "center", alignItems: "center" }}
                >
                    <Typography variant="h1">Webhooks</Typography>
                </Stack>
                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    spacing={2}
                >
                    {/* Filters Menu */}
                    <FiltersMenu
                        eventTypeFilter={eventTypeFilter}
                        setEventTypeFilter={setEventTypeFilter}
                        statusFilter={statusFilter}
                        setStatusFilter={setStatusFilter}
                        setPageAction={setPageAction}
                    />

                    {/* Search Bar */}
                    <TextField
                        id="search"
                        sx={{
                            maxWidth: "200px",
                            "& .MuiInputBase-root": {
                                paddingLeft: "14px !important"
                            }
                        }}
                        value={searchQuery}
                        placeholder="Search"
                        onChange={({ target: { value } }) =>
                            onSearchChange(value)
                        }
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon
                                        sx={{
                                            width: "18px",
                                            height: "18px",
                                            color: `${themePalette.helperText.main}`
                                        }}
                                    />
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery &&
                                searchQuery.length > 0 && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            aria-label="clear"
                                            onClick={handleClearSearch}
                                        >
                                            <ClearIcon
                                                sx={{
                                                    width: "18px",
                                                    height: "18px"
                                                }}
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                )
                        }}
                    />

                    {/* Add Webhook Button */}
                    <Button
                        textTransform="uppercase"
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon sx={{ color: "white" }} />}
                        onClick={() => setOpenAddWebhookModal(true)}
                    >
                        <Typography
                            variant="button"
                            sx={{ fontWeight: "600", color: "white" }}
                        >
                            Add Webhook
                        </Typography>
                    </Button>
                </Stack>
            </Stack>
        </ThemeProvider>
    );
};

function FiltersMenu({
    eventTypeFilter,
    setEventTypeFilter,
    statusFilter,
    setStatusFilter,
    setPageAction
}) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const theme = useTheme();

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleStatusChange = (event) => {
        setStatusFilter(event.target.value);
        setPageAction(0);
    };

    const handleEventTypeToggle = (eventType) => {
        setEventTypeFilter((prev) => {
            if (prev.includes(eventType)) {
                return prev.filter((et) => et !== eventType);
            }
            return [...prev, eventType];
        });
        setPageAction(0);
    };

    return (
        <>
            <TextField
                sx={{ maxWidth: "175px" }}
                value={"Filters"}
                onClick={handleClick}
                fullWidth
                InputProps={{
                    sx: {
                        color: theme.palette.text.main,
                        fontSize: "14px",
                        cursor: "pointer",
                        ...(open && {
                            "& fieldset": {
                                border: `2px solid ${theme.palette.primary.main}`
                            }
                        })
                    },
                    endAdornment: (
                        <InputAdornment position="start">
                            {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                        </InputAdornment>
                    ),
                    readOnly: true
                }}
                inputProps={{
                    sx: {
                        cursor: "pointer"
                    }
                }}
            />
            <ThemeProvider theme={dropDownMenuTheme(theme)}>
                <Menu
                    id="webhooks-filter-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                        "aria-labelledby": "filter-button",
                        sx: {
                            width: anchorEl && anchorEl.offsetWidth,
                            padding: "10px 15px"
                        }
                    }}
                >
                    {/* Status Filter */}
                    <FormControl>
                        <FormLabel id="status-filter-label">
                            <Typography variant="subHeading">Status</Typography>
                        </FormLabel>
                        <RadioGroup
                            aria-labelledby="status-filter-label"
                            name="status-filter"
                            value={statusFilter}
                            onChange={handleStatusChange}
                        >
                            {WEBHOOK_STATUS_OPTIONS.map((option) => (
                                <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    control={<Radio disableRipple />}
                                    label={option.label}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>

                    {/* Event Type Filter */}
                    <FormControl>
                        <FormLabel id="event-type-filter-label">
                            <Typography variant="subHeading">
                                Event Type
                            </Typography>
                        </FormLabel>
                        {WEBHOOK_EVENT_TYPE_OPTIONS.map((option) => (
                            <FormControlLabel
                                key={option.value}
                                control={
                                    <Checkbox
                                        checked={eventTypeFilter.includes(
                                            option.value
                                        )}
                                        onChange={() =>
                                            handleEventTypeToggle(option.value)
                                        }
                                        disableRipple
                                    />
                                }
                                label={option.label}
                            />
                        ))}
                    </FormControl>
                </Menu>
            </ThemeProvider>
        </>
    );
}

export default WebhooksPage;
